import React, { useCallback, useState } from 'react';
import { Button, Form, Modal, ModalProps } from 'react-bootstrap';
import { Auth, CognitoUser } from '@aws-amplify/auth';
import { E164Number } from 'libphonenumber-js/core';

import { useAutoSignInContext, useHandleError } from '@/hooks';
import { LoadingButton, PhoneNumberInput } from '@/components';
import { publicService } from '@/lib/services';
import { ACTION_STATUS, ACTION_TYPES } from '@/lib/models';
import { trackAction, toErrorFields } from '@/lib/utils';

const DialogAs = (dialogProps: any) => (
	<Modal.Dialog
		{...dialogProps}
		style={{ maxWidth: 'none', width: 'fit-content', marginLeft: 'auto', marginRight: 'auto' }}
	/>
);

interface MfaSmsPhoneModalProps extends ModalProps {
	username: string;
	firstTimeSetup: boolean;
	onBack(): void;
	onCodeSent(fauxSignedInUser?: CognitoUser): void;
}

export const MfaSmsPhoneModal = ({
	username,
	firstTimeSetup,
	onBack,
	onCodeSent,
	...props
}: MfaSmsPhoneModalProps) => {
	const handleError = useHandleError();
	const { emailAddress, password } = useAutoSignInContext();

	const [isLoading, setIsLoading] = useState(false);
	const [phoneNumberInputValue, setPhoneNumberInputValue] = useState<E164Number>();

	const handleOnEnter = useCallback(() => {
		setIsLoading(false);
		setPhoneNumberInputValue('');
	}, []);

	async function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		setIsLoading(true);

		if (firstTimeSetup) {
			try {
				await publicService
					.updateAndVerifyUserPhoneNumber({
						username,
						phoneNumber: String(phoneNumberInputValue),
					})
					.fetch();
				trackAction({
					actionType: ACTION_TYPES.PHONE_NUMBER_SUBMITTED,
					status: ACTION_STATUS.SUCCESS,
					emailAddress,
				});
				const signedInUser = await Auth.signIn(emailAddress, password);
				onCodeSent(signedInUser);
			} catch (error) {
				trackAction({
					actionType: ACTION_TYPES.PHONE_NUMBER_SUBMITTED,
					status: ACTION_STATUS.FAILURE,
					emailAddress,
					...toErrorFields(error),
				});
				handleError(error);
			} finally {
				setIsLoading(false);
			}
			return;
		}

		try {
			const currentAuthenticatedUser: CognitoUser = await Auth.currentAuthenticatedUser();
			await Auth.updateUserAttributes(currentAuthenticatedUser, { phone_number: phoneNumberInputValue });
			await Auth.verifyUserAttribute(currentAuthenticatedUser, 'phone_number');
			trackAction({
				actionType: ACTION_TYPES.PHONE_NUMBER_SUBMITTED,
				status: ACTION_STATUS.SUCCESS,
				emailAddress: currentAuthenticatedUser.getUsername?.() ?? emailAddress,
			});
			onCodeSent();
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.PHONE_NUMBER_SUBMITTED,
				status: ACTION_STATUS.FAILURE,
				emailAddress,
				...toErrorFields(error),
			});
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Modal centered onEnter={handleOnEnter} dialogAs={DialogAs} {...props}>
			<Modal.Header>
				<Modal.Title>Set up SMS MFA</Modal.Title>
			</Modal.Header>
			<Form onSubmit={handleFormSubmit}>
				<Modal.Body style={{ paddingTop: 20, paddingBottom: 20 }}>
					<p className="mb-3">Enter the phone number that will receive the verification code via SMS.</p>
					<Form.Group className="mb-0">
						<Form.Label>Phone Number</Form.Label>
						<PhoneNumberInput
							value={phoneNumberInputValue}
							onChange={setPhoneNumberInputValue}
							disabled={isLoading}
							required
						/>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex align-items-center w-100" style={{ gap: 8 }}>
						<Button
							variant="outline-primary"
							onClick={onBack}
							className="d-flex justify-content-center align-items-center text-nowrap"
							style={{ flex: '1 1 0', minWidth: 0 }}
							disabled={isLoading}
						>
							Cancel
						</Button>
						<LoadingButton
							isLoading={isLoading}
							disabled={!phoneNumberInputValue}
							variant="primary"
							type="submit"
							className="d-flex justify-content-center align-items-center text-nowrap"
							style={{ flex: '1 1 0', minWidth: 0 }}
						>
							Send Code
						</LoadingButton>
					</div>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};
